/**
 * API-backed {@link SessionsRepository}.
 *
 * - `listMySessions` reads the live, Cognito-authenticated `GET /sessions/mine`.
 * - `getSession` reads one session with its full GPS evidence via
 *   `GET /sessions/mine?uuid=` (list rows omit the heavy track fields).
 * - `submitLifecycleEvent` calls the live `POST /sessions/state` endpoint.
 */

import type { SessionLifecycleEvent, Session } from '@shared/sessions/types'
import type { JourneyPingInput, LifecycleEventResult, PreShiftCompleteResult, SessionsRepository } from './sessions-repository'
import {
  getMySession,
  getMySessions,
  postDeparturePreview,
  postSessionState,
  type SessionsAudience,
} from './sessions-api-client'
import {
  getSessionJourney,
  postCancelRequest,
  postJourneyArrive,
  postJourneyBackfill,
  postJourneyPing,
  postJourneyStart,
  postPreShiftChecklist,
} from './sessions-api-client'
import type { CancellationReason } from '@shared/sessions/types'
import {
  sessionFollowingUserId,
  updateSessionFollowingFromList,
} from '../../../stores/session-following-store'
import type { JourneyBackfillFix } from '@shared/sessions/journey-backfill'

export class ApiSessionsRepository implements SessionsRepository {
  /** Last resolved audience from the list endpoint (for UI branching). */
  lastAudience: SessionsAudience = 'provider'

  async listMySessions(): Promise<import('@shared/sessions/types').Session[]> {
    const requestedFor = sessionFollowingUserId()
    const result = await getMySessions()
    this.lastAudience = result.audience
    if (result.audience === 'linked_independent') {
      updateSessionFollowingFromList(result.followedMembers, requestedFor)
    }
    return result.sessions
  }

  getAudience(): SessionsAudience {
    return this.lastAudience
  }

  async getSession(uuid: string): Promise<Session | null> {
    const result = await getMySession(uuid)
    return result?.session ?? null
  }

  async submitLifecycleEvent(event: SessionLifecycleEvent): Promise<LifecycleEventResult> {
    const res = await postSessionState(event)
    return {
      uuid: res.uuid,
      status: res.status,
      lifecycleState: res.lifecycleState,
      kmTracked: res.kmTracked,
      eventType: event.type,
      ahpSync: res.ahpSync,
      ...(res.patch ? { patch: res.patch } : {}),
    }
  }

  async previewDeparture(event: SessionLifecycleEvent, opts?: { signal?: AbortSignal }) {
    return postDeparturePreview(event, opts?.signal)
  }

  async getSessionJourney(uuid: string) {
    return getSessionJourney(uuid)
  }

  async savePreShiftChecklist(
    uuid: string,
    body: {
      responses: Record<string, boolean>
      completed?: boolean
      providerLocation?: { lat: number; lng: number }
    },
  ): Promise<PreShiftCompleteResult> {
    const res = await postPreShiftChecklist(uuid, body)
    return {
      checklist: res.preShiftChecklist,
      leaveByAt: res.leaveByAt,
      travelDurationMs: res.travelDurationMs,
    }
  }

  async startJourney(uuid: string, ping: JourneyPingInput) {
    const res = await postJourneyStart(uuid, ping)
    return { journey: res.journey, leaveByAt: res.leaveByAt }
  }

  async pingJourney(uuid: string, ping: JourneyPingInput) {
    const res = await postJourneyPing(uuid, ping)
    return res.journey
  }

  async backfillJourney(uuid: string, fixes: JourneyBackfillFix[]) {
    const res = await postJourneyBackfill(uuid, fixes)
    return res.journey
  }

  async arriveJourney(
    uuid: string,
    ping?: Pick<JourneyPingInput, 'lat' | 'lng' | 'timestamp' | 'accuracy'>,
  ) {
    const res = await postJourneyArrive(uuid, ping)
    return res.journey
  }

  async requestCancellation(uuid: string, input: { reason: CancellationReason; note?: string }) {
    const res = await postCancelRequest(uuid, input)
    return { status: res.status, cancellationRequested: res.cancellationRequested }
  }
}
